import * as Sentry from "@sentry/react"
import type { Breadcrumb, ErrorEvent } from "@sentry/types"

import { sanitizeSentryBreadcrumb, sanitizeSentryEvent } from "@/shared/sentry-sanitize"

const RATE_LIMIT_KEY = "bibleImposter.sentryRate"
const RATE_LIMIT_WINDOW_MS = 5 * 60 * 1000
const RATE_LIMIT_MAX_EVENTS = 10
declare const __GIT_SHA__: string

initSentry()

function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) return

  Sentry.init({
    dsn,
    release: import.meta.env.VITE_GIT_SHA ?? __GIT_SHA__,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
    sampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend: beforeSendWithRateLimit,
    beforeBreadcrumb: sanitizeBreadcrumb,
    integrations: [Sentry.browserApiErrorsIntegration(), Sentry.dedupeIntegration()],
  })
}

export function captureClientException(error: unknown, context?: Record<string, unknown>) {
  Sentry.captureException(error, context ? { extra: context } : undefined)
}

export function reactRootErrorHandler() {
  return Sentry.reactErrorHandler()
}

export function beforeSendWithRateLimit(event: ErrorEvent) {
  if (!allowSentryEvent()) {
    return null
  }

  return sanitizeSentryEvent(event)
}

export function resetSentryRateLimitForTests() {
  sessionStorage.removeItem(RATE_LIMIT_KEY)
}

function sanitizeBreadcrumb(breadcrumb: Breadcrumb) {
  return sanitizeSentryBreadcrumb(breadcrumb)
}

function allowSentryEvent() {
  if (typeof sessionStorage === "undefined") return true

  const now = Date.now()
  const current = readRateLimitWindow(now)

  if (current.count >= RATE_LIMIT_MAX_EVENTS) {
    return false
  }

  sessionStorage.setItem(
    RATE_LIMIT_KEY,
    JSON.stringify({
      windowStart: current.windowStart,
      count: current.count + 1,
    })
  )
  return true
}

function readRateLimitWindow(now: number) {
  const fallback = { windowStart: now, count: 0 }
  if (typeof sessionStorage === "undefined") return fallback

  const raw = sessionStorage.getItem(RATE_LIMIT_KEY)

  if (!raw) {
    return fallback
  }

  try {
    const parsed = JSON.parse(raw) as { windowStart?: number; count?: number }
    if (
      typeof parsed.windowStart === "number" &&
      typeof parsed.count === "number" &&
      now - parsed.windowStart < RATE_LIMIT_WINDOW_MS
    ) {
      return { windowStart: parsed.windowStart, count: parsed.count }
    }
  } catch {
    return fallback
  }

  return fallback
}
